// AVÉLIS PRO — Notifications push côté client (pro & collaborateurs)
// L'abonnement (souscription) se fait ici, côté navigateur authentifié.
// L'ENVOI du push, lui, est fait côté serveur (netlify/functions/send-push),
// car il nécessite la clé privée VAPID (jamais exposée au client).
import { supabase } from './supabase';

// Clé publique VAPID — NON secrète (la privée est une variable d'env Netlify).
const VAPID_PUBLIC_KEY = 'BK9G7TWl56n3rdf1_vCRV8AMj08RXDGI2qj7uy0Jqqh-yAnTEVOAAH_TuMI6-f91BYSfid9d6D6gaB_x6NpppSE';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// Le navigateur sait-il faire du push ? (iPhone : uniquement en PWA « écran d'accueil »)
export function pushSupporte() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

// État courant : 'non-supporte' | 'refuse' | 'a-activer' | 'actif'
export async function etatNotifications() {
  if (!pushSupporte()) return 'non-supporte';
  if (Notification.permission === 'denied') return 'refuse';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub && Notification.permission === 'granted') return 'actif';
  } catch (_) {}
  return 'a-activer';
}

// Active les notifs pour l'utilisateur courant (pro OU collab).
// DOIT être appelé suite à un clic (iOS refuse la demande de permission hors geste utilisateur).
// Retourne { ok, raison }.
export async function activerNotifications(userId) {
  if (!pushSupporte()) return { ok: false, raison: 'non-supporte' };
  if (!userId) return { ok: false, raison: 'no-user' };

  let perm = Notification.permission;
  if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (_) { perm = 'default'; } }
  if (perm !== 'granted') return { ok: false, raison: perm === 'denied' ? 'refuse' : 'annule' };

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const json = sub.toJSON();
    const { error } = await supabase.from('push_subscriptions').upsert({
      user_id: userId,
      endpoint: sub.endpoint,
      p256dh: json.keys && json.keys.p256dh,
      auth: json.keys && json.keys.auth,
      user_agent: (navigator.userAgent || '').slice(0, 300),
    }, { onConflict: 'endpoint' });
    if (error) { console.warn('[push] enregistrement échec', error); return { ok: false, raison: 'db' }; }
    return { ok: true };
  } catch (e) {
    console.warn('[push] souscription échec', e);
    return { ok: false, raison: 'erreur' };
  }
}

// Désactive les notifs sur CET appareil (désabonne + retire de la base).
export async function desactiverNotifications() {
  if (!pushSupporte()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint); } catch (_) {}
      try { await sub.unsubscribe(); } catch (_) {}
    }
  } catch (e) { console.warn('[push] désactivation échec', e); }
}

// ── Côté CLIENTE (espace client, sans compte) ────────────────────────────────
// Elle n'a pas d'identifiant Supabase : l'abonnement part vers la fonction
// serveur avec son jeton d'espace, et c'est le serveur qui le range sous son
// identité (téléphone normalisé / email) — déjà vérifiée à l'identification.
export async function activerNotifsCliente(token) {
  if (!pushSupporte()) return { ok: false, raison: 'non-supporte' };
  if (!token) return { ok: false, raison: 'no-token' };

  let perm = Notification.permission;
  if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (_) { perm = 'default'; } }
  if (perm !== 'granted') return { ok: false, raison: perm === 'denied' ? 'refuse' : 'annule' };

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const res = await fetch('/.netlify/functions/espace-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'push', token, subscription: sub.toJSON(), userAgent: (navigator.userAgent || '').slice(0, 300) }),
    });
    if (!res.ok) return { ok: false, raison: 'db' };
    return { ok: true };
  } catch (e) {
    console.warn('[push] souscription cliente échec', e);
    return { ok: false, raison: 'erreur' };
  }
}

export async function desactiverNotifsCliente(token) {
  if (!pushSupporte()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    try {
      await fetch('/.netlify/functions/espace-client', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'push_off', token, endpoint: sub.endpoint }),
      });
    } catch (_) {}
    try { await sub.unsubscribe(); } catch (_) {}
  } catch (e) { /* rien à désactiver */ }
}

// Envoie une notif push au(x) pro(s) concerné(s). Best-effort, jamais bloquant.
// { proId, collabId } : le titulaire du compte ET le collaborateur concerné reçoivent tous les deux.
export async function notifierPro({ proId, collabId, type, ...data }) {
  try {
    await fetch('/.netlify/functions/send-push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proId, collabId, type, ...data }),
    });
  } catch (_) { /* non bloquant — le push est un bonus, jamais critique */ }
}
